import { useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { reaction } from 'mobx'
import { useRepository } from './auth'

/**
 * Hook that listens for grade sync job completion and shows toasts.
 * Should be called at the app level to persist across navigation.
 * Uses the GradeSyncJobsCubit on BreakoutUser to manage the stream.
 */
export function useGradeSyncNotification() {
  const repository = useRepository()
  const { t } = useTranslation()

  useEffect(() => {
    const user = repository.breakoutUser
    if (!user) return

    // Access the cubit to ensure the stream is started
    const cubit = user.gradeSyncJobs

    // React to completed jobs
    const completedDisposer = reaction(
      () => cubit.completedJobsToProcess,
      (completedJobs) => {
        for (const job of completedJobs) {
          toast.success(t('instructor_class.grade_sync_complete'))
          cubit.markJobAsProcessed(job.id)
        }
      },
      { fireImmediately: true }
    )

    // React to failed jobs
    const failedDisposer = reaction(
      () => cubit.failedJobsToProcess,
      (failedJobs) => {
        for (const job of failedJobs) {
          toast.error(t('instructor_class.grade_sync_failed'))
          cubit.markJobAsProcessed(job.id)
        }
      },
      { fireImmediately: true }
    )

    return () => {
      completedDisposer()
      failedDisposer()
    }
  }, [repository, t])
}
